import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { pickLocalized } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Pencil, Clock, Table2, CheckCircle2 } from "lucide-react";
// Relative, not "@/...": there is no "@" alias under Jest — see FieldError.jsx's own
// header for the precedent this follows.
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { limitToText, textToLimit } from "./limitText";

// Mirrors backend/plan_features.py's CAPABILITY_KEY_RE exactly (contract §11) so a
// malformed key is refused at click-time instead of round-tripping to the 422.
const CAPABILITY_KEY_RE = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;

/**
 * Adapts the wire shape's { <field>, <field>_i18n: {fr, es} } pair (capabilities and
 * groups both use it — contract §3.1/3.2) to the flat "_en"/"_fr"/"_es" suffix shape
 * `pickLocalized` (frontend/src/i18n/pickLocalized.js) already knows how to resolve.
 * The canonical value is written under the "_en" suffix explicitly — never left
 * implicit — so an "en" reader's chain (["_en","_fr"]) resolves to it directly instead
 * of falling through to a French override that happens to be present. Missing fr/es
 * overlays degrade to this same canonical EN, matching the project's EN-default law.
 */
function localize(canonical, i18nOverlay, field, lang) {
  return pickLocalized(
    { [`${field}_en`]: canonical, [`${field}_fr`]: i18nOverlay?.fr, [`${field}_es`]: i18nOverlay?.es },
    field,
    lang,
  );
}

const setsEqual = (a, b) => {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
};

/** Normalizes any of the §5 structured `detail` bodies into a message a human can read. */
function describeCapabilityError(e, t) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (status === 422 && detail?.code === "UNKNOWN_CAPABILITY") {
    return t("pfm.unknownCapabilityError", { keys: (detail.keys || []).join(", ") });
  }
  if (status === 409 && detail?.code === "CAPABILITY_IN_USE") {
    return t("pfm.capabilityInUse", { plans: (detail.plans || []).join(", ") });
  }
  if (status === 409 && detail?.code === "CAPABILITY_EXISTS") {
    return t("pfm.capabilityExists", { key: detail.key });
  }
  if (status === 409 && detail?.code === "CAPABILITY_IS_CODE_OWNED") {
    return t("pfm.codeOwnedError", { fields: (detail.fields || []).join(", ") });
  }
  return extractApiError(e, t("pfm.genericError"), t);
}

const emptyCustomForm = () => ({
  mode: "create",
  key: "",
  label: "",
  label_fr: "",
  label_es: "",
  group: "",
  status: "live",
  eta: "",
  order: "100",
});

function computeCustomErrors(t, form, groups) {
  const errors = {};
  if (form.mode === "create") {
    if (!CAPABILITY_KEY_RE.test(form.key || "")) errors.key = t("pfm.customKeyInvalid");
    if (!groups.some((g) => g.key === form.group)) errors.group = t("pfm.groupRequired");
  }
  if (!form.label?.trim()) errors.label = t("pfm.labelRequired");
  if (!/^\d+$/.test(String(form.order ?? "").trim())) errors.order = t("pfm.orderInvalid");
  return Object.keys(errors).length ? errors : null;
}

/**
 * One capability row: label + status marker, then the plans' cells. Three shapes:
 *   - a CORE row (`cap.core`) is granted to every tenant whatever its plan, so it has no
 *     per-plan cell at all — one statement across the columns, nothing to toggle;
 *   - a LIMIT row (`kind: "limit"`) grants a NUMBER, edited per plan (blank = unlimited);
 *     a coming-soon limit row shows the stored number read-only and is never saved;
 *   - every other row is a per-plan switch. A coming-soon row keeps its switches: a plan
 *     may promise a feature ahead of its ship date, and the badge says it is not built.
 * There is no "not enforced" marker: every live, non-core capability is gated.
 */
function CapabilityRow({ cap, plans, draft, onToggle, limitDraft, limitErrors, onLimitChange, lang, t, onEditCustom }) {
  const label = localize(cap.label, cap.label_i18n, "label", lang);
  const isComingSoon = cap.status === "coming_soon";
  const isLimit = cap.kind === "limit";

  return (
    <tr className={cap.active ? "" : "opacity-50"}>
      <td className="px-4 py-3 align-top">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium text-sm text-slate-800">{label}</span>
          {cap.origin === "custom" && (
            <button
              type="button"
              onClick={() => onEditCustom(cap)}
              data-testid={`pfm-custom-edit-${cap.key}`}
              className="text-slate-400 hover:text-slate-700"
              title={t("pfm.customEdit")}
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
          {isComingSoon && (
            <Badge variant="secondary" data-testid={`pfm-status-${cap.key}`} className="gap-1">
              <Clock className="w-3 h-3" />
              {t("pfm.comingSoonBadge")}
              {/* No eta is a real state, no date announced: the badge says "Coming soon" alone. */}
              {cap.eta && <span data-testid={`pfm-eta-${cap.key}`}>{cap.eta}</span>}
            </Badge>
          )}
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5">{cap.key}</div>
      </td>
      {cap.core && (
        <td colSpan={plans.length} className="px-4 py-3 text-center align-top">
          <span
            data-testid={`pfm-core-${cap.key}`}
            className="inline-flex items-center gap-1.5 text-sm text-emerald-700"
          >
            <CheckCircle2 className="w-4 h-4" /> {t("pfm.coreIncluded")}
          </span>
        </td>
      )}
      {!cap.core && plans.map((p) => {
        if (isLimit && isComingSoon) {
          // A coming-soon limit has nothing to limit yet: the plan's stored number is
          // shown, read-only, and is never part of a save.
          return (
            <td key={p.plan_id} className="px-4 py-3 text-center align-top">
              <Input
                disabled
                value={limitToText(p[cap.limit_field])}
                placeholder={t("pfm.limitPlaceholder")}
                aria-label={`${label} — ${p.name}`}
                data-testid={`pfm-limit-${p.plan_id}-${cap.key}`}
                className="w-28 mx-auto text-center"
              />
            </td>
          );
        }
        if (isLimit) {
          const invalid = !!limitErrors[p.plan_id]?.[cap.limit_field];
          return (
            <td key={p.plan_id} className="px-4 py-3 text-center align-top">
              <Input
                inputMode="numeric"
                value={limitDraft[p.plan_id]?.[cap.limit_field] ?? ""}
                onChange={(e) => onLimitChange(p.plan_id, cap.limit_field, e.target.value)}
                placeholder={t("pfm.limitPlaceholder")}
                title={t("pfm.limitInputHint")}
                aria-label={`${label} — ${p.name}`}
                aria-invalid={invalid || undefined}
                data-testid={`pfm-limit-${p.plan_id}-${cap.key}`}
                className={`w-28 mx-auto text-center ${invalid ? invalidInputClass : ""}`}
              />
            </td>
          );
        }
        const checked = draft[p.plan_id]?.has(cap.key) || false;
        return (
          <td key={p.plan_id} className="px-4 py-3 text-center align-top">
            <Switch
              checked={checked}
              onCheckedChange={() => onToggle(p.plan_id, cap.key)}
              data-testid={`pfm-cell-${p.plan_id}-${cap.key}`}
            />
          </td>
        );
      })}
    </tr>
  );
}

export default function PlanFeatureMatrix() {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const [groups, setGroups] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [plans, setPlans] = useState([]);
  const [original, setOriginal] = useState({});
  const [draft, setDraft] = useState({});
  // Limit numbers, as the TEXT of each cell: { [plan_id]: { [limit_field]: "25" | "" } }.
  const [originalLimits, setOriginalLimits] = useState({});
  const [limitDraft, setLimitDraft] = useState({});
  const [limitErrors, setLimitErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [customOpen, setCustomOpen] = useState(false);
  const [customForm, setCustomForm] = useState(emptyCustomForm());
  const [customErrors, setCustomErrors] = useState(null);
  const [customBusy, setCustomBusy] = useState(false);

  // Deliberately does NOT clear `error`. `save()` sets the failure message and then reloads
  // to resync against the server; a `setError("")` here wiped that message on the way out, so
  // a failed save rendered nothing at all and read as a silent no-op. Clearing is the
  // responsibility of whoever STARTS a user action (see `save`), not of the read that follows
  // it — `load`'s own catch below still reports a load failure.
  const load = async () => {
    setLoading(true);
    try {
      const [capRes, planRes] = await Promise.all([
        api.get("/super-admin/capabilities"),
        api.get("/super-admin/plans"),
      ]);
      const caps = capRes.data?.capabilities || [];
      setGroups(capRes.data?.groups || []);
      setCapabilities(caps);
      const planList = planRes.data || [];
      setPlans(planList);
      const orig = {};
      planList.forEach((p) => { orig[p.plan_id] = new Set(p.capabilities || []); });
      setOriginal(orig);
      setDraft(Object.fromEntries(Object.entries(orig).map(([k, v]) => [k, new Set(v)])));
      // The limit fields are named by the catalogue's limit rows, never hardcoded here.
      // Only LIVE limit rows are editable, hence the only fields a save can carry.
      const limitFields = caps
        .filter((c) => c.kind === "limit" && c.limit_field && c.status === "live")
        .map((c) => c.limit_field);
      const origLimits = {};
      planList.forEach((p) => {
        origLimits[p.plan_id] = Object.fromEntries(limitFields.map((f) => [f, limitToText(p[f])]));
      });
      setOriginalLimits(origLimits);
      setLimitDraft(Object.fromEntries(Object.entries(origLimits).map(([k, v]) => [k, { ...v }])));
      setLimitErrors({});
    } catch (e) {
      setError(describeCapabilityError(e, t));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedPlans = useMemo(() => [...plans].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [plans]);
  const sortedGroups = useMemo(() => [...groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [groups]);
  const capsByGroup = useMemo(() => {
    const out = {};
    sortedGroups.forEach((g) => { out[g.key] = []; });
    [...capabilities].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).forEach((c) => {
      if (!out[c.group]) {
        // Contract invariant #8 guarantees every capability's group exists in
        // CAPABILITY_GROUPS; a miss here means the catalogue and the group list
        // disagree — surfaced in dev, never silently dropped or mis-bucketed.
        if (process.env.NODE_ENV !== "production") {
          console.warn(`[PlanFeatureMatrix] capability "${c.key}" references unknown group "${c.group}"`);
        }
        return;
      }
      out[c.group].push(c);
    });
    return out;
  }, [capabilities, sortedGroups]);

  const dirtyCount = useMemo(() => {
    let n = 0;
    Object.keys(draft).forEach((pid) => {
      const o = original[pid] || new Set();
      const d = draft[pid] || new Set();
      o.forEach((k) => { if (!d.has(k)) n += 1; });
      d.forEach((k) => { if (!o.has(k)) n += 1; });
    });
    Object.keys(limitDraft).forEach((pid) => {
      Object.entries(limitDraft[pid] || {}).forEach(([field, text]) => {
        if (text.trim() !== (originalLimits[pid]?.[field] ?? "")) n += 1;
      });
    });
    return n;
  }, [draft, original, limitDraft, originalLimits]);

  const toggleCell = (planId, key) => {
    setDraft((prev) => {
      const set = new Set(prev[planId] || []);
      if (set.has(key)) set.delete(key); else set.add(key);
      return { ...prev, [planId]: set };
    });
  };

  const changeLimit = (planId, field, text) => {
    setLimitDraft((prev) => ({ ...prev, [planId]: { ...(prev[planId] || {}), [field]: text } }));
    setLimitErrors((prev) => (prev[planId]?.[field] ? { ...prev, [planId]: { ...prev[planId], [field]: false } } : prev));
  };

  /**
   * One PUT per plan that changed, carrying only what changed on it: the capability list
   * and/or each edited limit number (blank → -1). Every limit cell is validated first, so
   * a typo in one plan never lets the others save half a matrix.
   */
  const save = async () => {
    const errors = {};
    const bodies = {};
    const planIds = new Set([...Object.keys(draft), ...Object.keys(limitDraft)]);
    planIds.forEach((pid) => {
      const body = {};
      if (!setsEqual(draft[pid] || new Set(), original[pid] || new Set())) {
        body.capabilities = Array.from(draft[pid]);
      }
      Object.entries(limitDraft[pid] || {}).forEach(([field, text]) => {
        if (text.trim() === (originalLimits[pid]?.[field] ?? "")) return;
        const value = textToLimit(text);
        if (value === null) {
          errors[pid] = { ...(errors[pid] || {}), [field]: true };
        } else {
          body[field] = value;
        }
      });
      if (Object.keys(body).length) bodies[pid] = body;
    });
    if (Object.keys(errors).length) {
      setLimitErrors(errors);
      setError(t("pfm.limitInvalid"));
      return;
    }
    const dirtyPlanIds = Object.keys(bodies);
    if (dirtyPlanIds.length === 0) return;
    setSaving(true);
    setError("");
    const results = await Promise.allSettled(
      dirtyPlanIds.map((pid) => api.put(`/super-admin/plans/${pid}`, bodies[pid])),
    );
    const failures = results.filter((r) => r.status === "rejected");
    if (failures.length > 0) {
      setError(failures.map((f) => describeCapabilityError(f.reason, t)).join(" ; "));
    } else {
      toast.success(t("pfm.saveOk"));
    }
    await load();
    setSaving(false);
  };

  // ---- custom capability dialog ----
  const openCreateCustom = () => {
    setCustomForm(emptyCustomForm());
    setCustomErrors(null);
    setCustomOpen(true);
  };
  const openEditCustom = (cap) => {
    setCustomForm({
      mode: "edit",
      key: cap.key,
      label: cap.label,
      label_fr: cap.label_i18n?.fr || "",
      label_es: cap.label_i18n?.es || "",
      group: cap.group,
      status: cap.status,
      eta: cap.eta || "",
      order: String(cap.order ?? 0),
    });
    setCustomErrors(null);
    setCustomOpen(true);
  };
  const updateCustomField = (field, value) => {
    setCustomForm((f) => ({ ...f, [field]: value }));
    setCustomErrors((prev) => (prev ? { ...prev, [field]: null } : prev));
  };

  const submitCustom = async () => {
    const err = computeCustomErrors(t, customForm, groups);
    if (err) { setCustomErrors(err); return; }
    setCustomErrors(null);
    setCustomBusy(true);
    const label_i18n = {};
    if (customForm.label_fr.trim()) label_i18n.fr = customForm.label_fr.trim();
    if (customForm.label_es.trim()) label_i18n.es = customForm.label_es.trim();
    // A blank eta is "no date announced", sent as null like the server stores it.
    const eta = customForm.status === "coming_soon" ? customForm.eta.trim() || null : null;
    try {
      if (customForm.mode === "create") {
        await api.post("/super-admin/capabilities", {
          key: customForm.key.trim(),
          group: customForm.group,
          label: customForm.label.trim(),
          label_i18n,
          description: "",
          description_i18n: {},
          status: customForm.status,
          eta,
          order: Number(customForm.order),
          active: true,
        });
        toast.success(t("pfm.customCreated"));
      } else {
        // PUT never carries key/group/status: those are CODE_OWNED_FIELDS (contract
        // §11) even for a custom-origin entry — only copy (label/eta/order/active) is
        // overridable after creation. Sending them at all risks a 409
        // CAPABILITY_IS_CODE_OWNED, so they are omitted rather than sent unchanged.
        await api.put(`/super-admin/capabilities/${customForm.key}`, {
          label: customForm.label.trim(),
          label_i18n,
          description: "",
          description_i18n: {},
          eta,
          order: Number(customForm.order),
          active: true,
        });
        toast.success(t("pfm.customUpdated"));
      }
      setCustomOpen(false);
      load();
    } catch (e) {
      toast.error(describeCapabilityError(e, t));
    } finally {
      setCustomBusy(false);
    }
  };

  const deleteCustom = async () => {
    if (!window.confirm(t("pfm.customDeleteConfirm", { key: customForm.key }))) return;
    setCustomBusy(true);
    try {
      await api.delete(`/super-admin/capabilities/${customForm.key}`);
      toast.success(t("pfm.customDeleted"));
      setCustomOpen(false);
      load();
    } catch (e) {
      toast.error(describeCapabilityError(e, t));
    } finally {
      setCustomBusy(false);
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;

  return (
    <div data-testid="plan-feature-matrix">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div>
          <h1 className="font-sol font-semibold text-4xl">{t("pfm.title")}</h1>
          <p className="text-slate-500 mt-1 max-w-3xl">{t("pfm.subtitle")}</p>
        </div>
        <Button
          onClick={openCreateCustom}
          data-testid="pfm-custom-add"
          variant="outline"
          className="rounded-full border-slate-200 shrink-0"
        >
          <Plus className="w-4 h-4 mr-2" /> {t("pfm.customAdd")}
        </Button>
      </div>

      <div
        data-testid="pfm-grandfather-banner"
        className="mb-4 text-sm text-sky-900 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3"
      >
        {t("pfm.grandfatherBanner")}
      </div>

      {error && (
        <div data-testid="pfm-error" className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 overflow-x-auto mb-4">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-400 text-xs uppercase tracking-wider">
              <th className="px-4 py-3">{t("pfm.capabilityHeader")}</th>
              {sortedPlans.map((p) => (
                <th key={p.plan_id} className="px-4 py-3 text-center" data-testid={`pfm-plan-col-${p.plan_id}`}>
                  {p.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedGroups.map((g) => (
              <React.Fragment key={g.key}>
                <tr data-testid={`pfm-group-${g.key}`} className="bg-slate-50/60">
                  <td colSpan={sortedPlans.length + 1} className="px-4 py-2 font-semibold text-slate-700 text-xs uppercase tracking-wider">
                    {localize(g.label, g.label_i18n, "label", lang)}
                  </td>
                </tr>
                {(capsByGroup[g.key] || []).map((cap) => (
                  <CapabilityRow
                    key={cap.key}
                    cap={cap}
                    plans={sortedPlans}
                    draft={draft}
                    onToggle={toggleCell}
                    limitDraft={limitDraft}
                    limitErrors={limitErrors}
                    onLimitChange={changeLimit}
                    lang={lang}
                    t={t}
                    onEditCustom={openEditCustom}
                  />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Link to="/super-admin/plans" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
          <Table2 className="w-3.5 h-3.5" /> {t("pfm.goToPlans")}
        </Link>
        <div className="flex items-center gap-3">
          <span data-testid="pfm-dirty-count" className="text-sm text-slate-500">
            {t("pfm.dirtyCount", { count: dirtyCount })}
          </span>
          <Button
            onClick={save}
            disabled={saving || dirtyCount === 0}
            data-testid="pfm-save"
            className="bg-sol-gold hover:bg-sol-gold/90 text-black rounded-full"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {t("pfm.save")}
          </Button>
        </div>
      </div>

      <Dialog open={customOpen} onOpenChange={(open) => { setCustomOpen(open); if (!open) setCustomErrors(null); }}>
        <DialogContent className="max-w-lg" data-testid="pfm-custom-dialog">
          <DialogHeader>
            <DialogTitle>{customForm.mode === "create" ? t("pfm.customCreateTitle") : t("pfm.customEditTitle")}</DialogTitle>
            <DialogDescription>{t("pfm.customSubtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">{t("pfm.customKey")}</Label>
              <Input
                value={customForm.key}
                onChange={(e) => updateCustomField("key", e.target.value)}
                disabled={customForm.mode === "edit"}
                data-testid="pfm-custom-key"
                className={`mt-1 ${customErrors?.key ? invalidInputClass : ""}`}
              />
              <p className={`text-[11px] mt-1 ${customErrors?.key ? "text-rose-500" : "text-slate-400"}`}>
                {customErrors?.key || t("pfm.customKeyHelp")}
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">{t("pfm.customLabel")}</Label>
                <Input
                  value={customForm.label}
                  onChange={(e) => updateCustomField("label", e.target.value)}
                  data-testid="pfm-custom-label"
                  className={`mt-1 ${customErrors?.label ? invalidInputClass : ""}`}
                />
                <FieldError message={customErrors?.label || ""} testId="pfm-custom-label-error" />
              </div>
              <div>
                <Label className="text-xs">{t("pfm.customLabelFr")}</Label>
                <Input
                  value={customForm.label_fr}
                  onChange={(e) => updateCustomField("label_fr", e.target.value)}
                  data-testid="pfm-custom-label-fr"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">{t("pfm.customLabelEs")}</Label>
                <Input
                  value={customForm.label_es}
                  onChange={(e) => updateCustomField("label_es", e.target.value)}
                  data-testid="pfm-custom-label-es"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">{t("pfm.customGroup")}</Label>
                <select
                  value={customForm.group}
                  onChange={(e) => updateCustomField("group", e.target.value)}
                  disabled={customForm.mode === "edit"}
                  data-testid="pfm-custom-group"
                  className={`mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm ${customErrors?.group ? invalidInputClass : "border-slate-200"}`}
                >
                  <option value="" disabled>—</option>
                  {groups.map((g) => (
                    <option key={g.key} value={g.key}>{localize(g.label, g.label_i18n, "label", lang)}</option>
                  ))}
                </select>
                <FieldError message={customErrors?.group || ""} testId="pfm-custom-group-error" />
              </div>
              <div>
                <Label className="text-xs">{t("pfm.customStatus")}</Label>
                <select
                  value={customForm.status}
                  onChange={(e) => updateCustomField("status", e.target.value)}
                  disabled={customForm.mode === "edit"}
                  data-testid="pfm-custom-status"
                  className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="live">{t("pfm.customStatusLive")}</option>
                  <option value="coming_soon">{t("pfm.customStatusComingSoon")}</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">{t("pfm.customOrder")}</Label>
                <Input
                  type="number"
                  value={customForm.order}
                  onChange={(e) => updateCustomField("order", e.target.value)}
                  data-testid="pfm-custom-order"
                  className={`mt-1 ${customErrors?.order ? invalidInputClass : ""}`}
                />
                <FieldError message={customErrors?.order || ""} testId="pfm-custom-order-error" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{t("pfm.customEta")}</Label>
              <Input
                value={customForm.eta}
                onChange={(e) => updateCustomField("eta", e.target.value)}
                disabled={customForm.status !== "coming_soon"}
                data-testid="pfm-custom-eta"
                className="mt-1"
              />
              <p className="text-[11px] mt-1 text-slate-400">{t("pfm.customEtaHelp")}</p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setCustomOpen(false)} className="flex-1 rounded-full">
                {t("pfm.customCancel")}
              </Button>
              {customForm.mode === "edit" && (
                <Button
                  onClick={deleteCustom}
                  disabled={customBusy}
                  variant="outline"
                  data-testid={`pfm-custom-delete-${customForm.key}`}
                  className="border-rose-200 text-rose-600 hover:bg-rose-50 rounded-full"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> {t("pfm.customDelete")}
                </Button>
              )}
              <Button
                onClick={submitCustom}
                disabled={customBusy}
                data-testid="pfm-custom-submit"
                className="flex-1 bg-sol-gold hover:bg-sol-gold/90 text-black rounded-full"
              >
                {customBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                {t("pfm.customSubmit")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
